import React from 'react';
import {connect} from 'react-redux';
import {getCellBgColor} from 'helpers/common-helper';
import PlayersInfo from 'desktop/elements/players/info';
import PlayerInjury from 'components/molecules/player-injury';
import {BasePlayersList} from 'components/organisms/players-list';
import PlayerLock from 'components/molecules/player-lock';
import TeamLogo from 'components/molecules/team-logo';
import PlayerExclusionState from 'components/molecules/player-exclusion/player-exclusion-state';
import Loader from 'components/atoms/loaders/loader';
import PlayerTeamStacking from 'components/molecules/player-team-stacking';
import Tooltip from 'components/atoms/tooltip';
import PlayerPosition from 'components/molecules/player-position';

class PlayersList extends BasePlayersList {
    componentDidUpdate(prevProps) {
        const {
            userPointsType,
            reset: {settings: resetSettings},
        } = this.props;
        const {userPointsType: prevUPType} = prevProps;

        if (this.wasSettingsReset(resetSettings)) {
            this.updateResetSettingsSignature(resetSettings);
            super.resetBaseState();
        } else if (!userPointsType.isUserPointsTypeEqual(prevUPType.getUserPointsType())) {
            super.resetBaseState({
                sortBy: {...this.state.sortBy},
            });
        }
    }

    getSearchConditions() {
        const {playersFilter} = this.props;
        let searchByConfirmed = {},
            searchByOrder = {};
        if (this.isNotExcludedMode(playersFilter.playersType)) {
            searchByConfirmed = this.getSearchRuleByConfirmed();
            searchByOrder = this.getSearchRuleByOrder();
        }

        return {
            ...this.getSearchRuleByMode({
                overallMode: {
                    checkPointsExclusion: true,
                    checkSalaryExclusion: true,
                },
            }),
            ...this.getSearchRuleByMultiPosition(),
            ...this.getSearchRuleByPlayerName(),
            ...this.getSearchRuleByTeamName(),
            ...searchByConfirmed,
            ...searchByOrder,
        };
    }

    render() {
        const {players, sportConfig, playersFilter} = this.props;

        if (!players.isPlayersGroupReady()) {
            return <Loader />;
        }

        const playersList = this.getPlayersForRendering();

        const rows = [];
        if (playersList.length > 0) {
            const shadingOptions = sportConfig.getShadingOptions();

            playersList.map((player, index) => {
                const userScoreBgColor = getCellBgColor(player.userScore, shadingOptions.userScore);
                const gradesBgColor = getCellBgColor(player.grades, shadingOptions.grades);
                const homeRunPriceBgColor =
                    Number(player.homeRunPrice) !== 0
                        ? getCellBgColor(player.homeRunPrice, shadingOptions.homeRunPrice)
                        : {};

                rows.push(
                    <tr key={index} data-pid={player.playerId} className={this.getRowClassName(player)}>
                        <td className="td-chk">
                            <PlayerExclusionState activeMode={playersFilter.playersType} playerId={player.getId()} />
                        </td>
                        <td className="td-chain">
                            <PlayerTeamStacking className={`chain-btn`} playerId={player.getId()} />
                        </td>
                        <td className="td-lock">
                            <PlayerLock className={`lock-btn`} playerId={player.getId()} />
                        </td>
                        <td className="td-icon">
                            <Tooltip
                                subject={
                                    player.isConfirmed() ? 'Position Confirmed: Yes' : 'Position Confirmed: Unknown'
                                }
                                iconClassName={player.isConfirmed() ? 'fa fa-check' : 'fa fa-question-circle'}
                            />
                        </td>
                        <td>
                            <span>{player.name}</span>
                            <PlayerInjury player={player} />
                        </td>
                        <td>{this.getPlayerSympathyComponent({player})}</td>
                        <td>
                            <PlayerPosition key={player.getId()} playerId={player.getId()} />
                        </td>
                        <td className="td-team">
                            <TeamLogo teamName={player.team} />
                        </td>
                        <td className="td-vs">{this.getOpponentsDelimiter(player)}</td>
                        <td className="td-team">
                            <TeamLogo teamName={player.opponent} />
                        </td>
                        <td>
                            <span>{player.hand}</span>
                        </td>
                        <td>
                            <span>{player.order}</span>
                        </td>
                        <td className="color-cell" style={{...homeRunPriceBgColor}}>
                            <span>{player.homeRunPrice}</span>
                        </td>
                        <td>
                            <span>{player.moneyLine}</span>
                        </td>
                        <td>
                            <span>{player.salary}</span>
                        </td>
                        <td className="color-cell" style={{...userScoreBgColor}}>
                            <span>{player.userScore}</span>
                        </td>
                        <td>
                            <span>{player.adminPoints}</span>
                        </td>
                        {this.getFinalPointsBlock({
                            points: player.fantasyPoints,
                        })}
                        <td className="color-cell" style={{...gradesBgColor}}>
                            <span>{player.grades}</span>
                        </td>
                        <td>
                            <span>{player.projOwn.getValue()}</span>
                        </td>
                        <td className="td-narrow">
                            {this.wrapContentInTooltip(
                                this.getPlayerPointsComponent({
                                    player,
                                }),
                                `Default Score: ${player.adminPoints}`,
                            )}
                        </td>
                        <td className="td-narrow">
                            {this.getPlayerExposureComponent({
                                playerId: player.getId(),
                                propName: 'exposureMin',
                            })}
                        </td>
                        <td className="td-narrow">
                            {this.getPlayerExposureComponent({
                                playerId: player.getId(),
                                propName: 'exposureMax',
                            })}
                        </td>
                    </tr>,
                );
            });
        } else {
            rows.push(
                <tr key="players-not-found">
                    <td colSpan="23" className="text-center">
                        Players were not found.
                    </td>
                </tr>,
            );
        }

        return (
            <div>
                <div className="main-table table-holder table-scroll">
                    <table className="table players-pool">
                        <thead>
                            <tr>
                                <th className="td-chk">
                                    {this.getPlayersGroupExclusionSwitcherComponent(playersList)}
                                </th>
                                <th className="td-chain">
                                    <span>TS </span>
                                    <Tooltip position={'left'} subject={'Use player only in MLB Team Stacking'} />
                                    <div>{this.getPlayerTeamStackingAllPlayersSwitcherComponent(playersList)}</div>
                                </th>
                                <th colSpan="4">
                                    <a
                                        className={super.getSortDirectionClassName('name')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'name')}
                                    >
                                        Name
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('position')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'position')}
                                    >
                                        Pos
                                    </a>
                                </th>
                                <th className="td-team">
                                    <a
                                        className={super.getSortDirectionClassName('team')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'team')}
                                    >
                                        Team
                                    </a>
                                </th>
                                <th className="td-vs"></th>
                                <th className="td-team">
                                    <a
                                        className={super.getSortDirectionClassName('opponent')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'opponent')}
                                    >
                                        Opp
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('hand')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'hand')}
                                    >
                                        Hand
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('order')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'order')}
                                    >
                                        Ord
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('homeRunPrice')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'homeRunPrice')}
                                    >
                                        HR odds
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('moneyLine')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'moneyLine')}
                                    >
                                        Money Line
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('salary')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'salary')}
                                    >
                                        Salary
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('userScore')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'userScore')}
                                    >
                                        Fpts/$
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('adminPoints')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'adminPoints')}
                                    >
                                        Proj
                                    </a>
                                </th>
                                {this.getFinalPointsHeaderSort()}
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('grades')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'grades')}
                                    >
                                        Rating
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('projected_ownership')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'projected_ownership')}
                                    >
                                        pOwn%
                                    </a>
                                </th>
                                <th>
                                    <a
                                        className={super.getSortDirectionClassName('userPoints')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'userPoints')}
                                    >
                                        FPts
                                    </a>
                                </th>
                                <th className="td-per">
                                    <a
                                        className={super.getSortDirectionClassName('exposureMin')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'exposureMin')}
                                    >
                                        Exp. min
                                    </a>
                                    {this.getGeneralExposureComponent({
                                        players: playersList,
                                        propName: 'exposureMin',
                                    })}
                                </th>
                                <th className="td-per">
                                    <a
                                        className={super.getSortDirectionClassName('exposureMax')}
                                        href="#"
                                        onClick={(e) => ::this.sortBy(e, 'exposureMax')}
                                    >
                                        Exp. max
                                    </a>
                                    {this.getGeneralExposureComponent({
                                        players: playersList,
                                        propName: 'exposureMax',
                                    })}
                                </th>
                            </tr>
                        </thead>

                        <tbody>{rows}</tbody>
                    </table>
                </div>

                <PlayersInfo playersQuantity={playersList.length} />
            </div>
        );
    }
}

function mapStatesToProps(state) {
    const {
        sportConfig,
        reset,
        playersLock,
        userPointsType,
        modesConfig,
        playersData: {players, filters, mlbFilters, games},
    } = state;

    return {
        players,
        games,
        playersFilter: filters,
        playersLock,
        sportConfig,
        mlbFilters,
        reset,
        userPointsType,
        modesConfig,
    };
}

const Table = connect(mapStatesToProps)(PlayersList);

export {Table};
